'use client'

import { useEffectOnce } from '@dni-react-hooks/use-effect-once'
import React, { createContext, useContext, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'

type ThemeProps = Readonly<{
  className?: string
  style?: Readonly<Record<string, string | number>>
}>

let portalProviderCount = 0

const PortalProviderContext = createContext<{
  portalProviderId: string | null
}>({
  portalProviderId: null,
})

export const usePortalProviderId = () => {
  const { portalProviderId } = useContext(PortalProviderContext)
  return portalProviderId
}

export interface PortalProviderProps {
  /** 子內容 */
  children: React.ReactNode
  /** 傳遞給 Portal 容器的主題屬性（className / style） */
  themeProps: ThemeProps
}

/**
 * PortalProvider 提供 Portal 的掛載容器。
 * 在 document.body 中建立一個帶有主題屬性的 div，作為所有 Portal 的渲染目標。
 */
export const PortalProvider = (props: PortalProviderProps) => {
  const { children, themeProps } = props
  const [portalProviderId, setPortalProviderId] = useState<string | null>(null)

  const portalProvider = useMemo(() => {
    if (portalProviderId !== null) {
      return createPortal(<div id={portalProviderId} {...themeProps} />, document.body)
    }
    return null
  }, [portalProviderId, themeProps])

  useEffectOnce(() => {
    setPortalProviderId(`pets-portal-provider-${portalProviderCount}`)
    portalProviderCount += 1
  })

  const contextValue = useMemo(() => ({
    portalProviderId,
  }), [portalProviderId])

  return (
    <PortalProviderContext.Provider value={contextValue}>
      {children}
      {portalProvider}
    </PortalProviderContext.Provider>
  )
}
